import { useEffect, useRef, useState } from "react";
import { BUILD_ID, fetchServedBuildId } from "../lib/build-id";

// Slim fixed bar that appears when the deployed build is newer than the one
// this tab booted with. SPA tabs keep their bundle until something reloads
// them — deploys never reach an open tab — so without this, "the fix is
// deployed" and "this tab shows the fix" silently diverge (it cost a day of
// prod testing on 2026-06-09). Mounted ABOVE Shell in main.tsx so it covers
// every surface this bundle serves: wizard, operator console, public pages.
//
// Checks: on mount, then whenever the tab becomes visible again (throttled).
// Quiet by design — no answer / dev server / same id → renders nothing.

const RECHECK_MIN_MS = 30_000;

// Inline styles, deliberately: the bar overlays surfaces with different
// palettes (cream wizard, operator console, public pages) and must not
// depend on any of their CSS being loaded or win specificity fights.
// Explicit font stack per the cross-platform rule (no system-ui).
const BAR: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  padding: "9px 16px",
  background: "#1a1814",
  color: "#fbf6ec",
  fontFamily: "'IBM Plex Sans', 'Inter', 'Segoe UI', Arial, sans-serif",
  fontSize: 14,
  lineHeight: 1.4,
  boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
};

const BUTTON: React.CSSProperties = {
  padding: "4px 14px",
  background: "#fbf6ec",
  color: "#1a1814",
  border: "1px solid #fbf6ec",
  borderRadius: 2,
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

export function StaleBuildBanner({
  currentId = BUILD_ID,
}: {
  /** Overridable for tests; defaults to this bundle's baked-in id. */
  currentId?: string;
}) {
  const [stale, setStale] = useState(false);
  const lastCheck = useRef(0);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      const now = Date.now();
      if (now - lastCheck.current < RECHECK_MIN_MS) return;
      lastCheck.current = now;
      const served = await fetchServedBuildId();
      if (cancelled || served === null) return;
      setStale(served !== currentId);
    }

    void check();
    function onVisible() {
      if (document.visibilityState === "visible") void check();
    }
    document.addEventListener("visibilitychange", onVisible);
    // Slow heartbeat for a tab that stays visible across a deploy (no
    // visibilitychange ever fires). A ~200-byte static fetch every 5 min.
    const beat = window.setInterval(() => {
      if (document.visibilityState === "visible") void check();
    }, 5 * 60_000);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(beat);
    };
  }, [currentId]);

  if (!stale) return null;
  return (
    <div style={BAR} role="status">
      <span>This page has been updated — reload to get the latest.</span>
      <button
        type="button"
        style={BUTTON}
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
    </div>
  );
}
